import React, { useState, useEffect, useRef } from "react";
import SideBar from "../components/SideBar";
import TopBar from "../components/TopBar";
import left from "../assets/image/left.svg";
import right from "../assets/image/right.svg";
import note from "../assets/image/note.svg";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Testimony() {
  const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;
  const getInitials = (name) => {
    if (!name) return "A";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [testimonies, setTestimonies] = useState([]);
  const hasTestimonies = testimonies.length > 0;
  const [formData, setFormData] = useState({
    testimony: "",
    is_shared_anonymously: false,
    is_featured: false,
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const scrollRef = useRef(null);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const scrollLeft = () => {
    if (scrollRef.current)
      scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    if (scrollRef.current)
      scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };

  const fetchFeaturedTestimonies = async () => {
    setFetching(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${TEST_BASE_URL}/featured-testimonies`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json();
      console.log("Featured Testimonies Response:", data);

      if (res.ok && data.status === "success") {
        setTestimonies(data.data || []);
      } else {
        toast.error(data.message || "Failed to fetch testimonies.", {
          theme: "colored",
        });
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error("Network error fetching testimonies.", { theme: "colored" });
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchFeaturedTestimonies();
  }, []);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.testimony) {
      toast.error("Please enter your testimony.", { theme: "colored" });
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("You must be logged in to share a testimony.", {
        theme: "colored",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${TEST_BASE_URL}/testimonies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: formData.testimony,
          is_shared_anonymously: formData.is_shared_anonymously,
          show_as_featured: formData.is_featured,
        }),
      });

      const data = await res.json();
      console.log("Testimony Response:", data);

      if (res.ok && data.status === "success") {
        toast.success("Your testimony was submitted successfully!", {
          theme: "colored",
        });
        setFormData({
          testimony: "",
          is_shared_anonymously: false,
          is_featured: false,
        });
        fetchFeaturedTestimonies();
      } else {
        if (data.errors) {
          Object.values(data.errors)
            .flat()
            .forEach((msg) => toast.error(msg, { theme: "colored" }));
        } else {
          toast.error(data.message || "Failed to submit testimony.", {
            theme: "colored",
          });
        }
      }
    } catch (err) {
      console.error("Error submitting testimony:", err);
      toast.error("Network error — please try again.", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (testimonies.length > 0) {
      const cards = document.querySelectorAll(".request-card");
      let maxHeight = 0;

      cards.forEach((card) => {
        card.style.height = "auto";
        maxHeight = Math.max(maxHeight, card.offsetHeight);
      });

      cards.forEach((card) => {
        card.style.height = `${maxHeight}px`;
      });
    }
  }, [testimonies]);

  return (
    <section className="dashboard">
      <SideBar isOpen={sidebarOpen} />
      <div className="body-content">
        <TopBar onToggleSidebar={toggleSidebar} />
        <div className="bottom-content">
          <div className="prayer-title">
            <span>Testimonies & Inspirations</span>
            <a href="/testimonyhistory">
              <u>Your Testimonies</u>
            </a>
          </div>

          <h6 className="py-3">Featured Testimonies</h6>

          <div className="testimony-wrapper">
            <img
              src={left}
              alt="scroll left"
              className={`arrow arrowleft ${!hasTestimonies ? "hidden" : ""}`}
              onClick={scrollLeft}
            />

            <div className="testimony-row" ref={scrollRef}>
              {fetching ? (
                <p>Loading testimonies...</p>
              ) : testimonies.length > 0 ? (
                testimonies.map((req, index) => (
                  <div className="request-card" key={index}>
                    <div className="testimony-avatar-wrapper">
                      {req.is_shared_anonymously ? (
                        <div className="testimony-avatar-fallback d-flex align-items-center justify-content-center">
                          A
                        </div>
                      ) : req.avatar ? (
                        <img
                          src={`https://rccgbournemouth.org/storage/${req.avatar}`}
                          alt={req.name || "Member"}
                          className="testimony-avatar-image"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            e.target.parentNode
                              .querySelector(".testimony-avatar-fallback")
                              ?.classList.remove("d-none");
                          }}
                        />
                      ) : (
                        <div className="testimony-avatar-fallback">
                          {getInitials(req.name)}
                        </div>
                      )}

                      {!req.is_shared_anonymously && (
                        <div
                          className="testimony-avatar-fallback d-none"
                          style={{ position: "absolute", inset: 0 }}
                        >
                          {getInitials(req.name)}
                        </div>
                      )}
                    </div>

                    <p className="">
                      <small>{req.message}</small>
                    </p>
                    <div className="testname">
                      <p className="text-primary">
                        <small>
                          {req.is_shared_anonymously
                            ? "Anonymous"
                            : req.name || "Anonymous"}
                        </small>
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p>No testimonies available yet.</p>
              )}
            </div>

            <img
              src={right}
              alt="scroll right"
              className={`arrow arrowright ${!hasTestimonies ? "hidden" : ""}`}
              onClick={scrollRight}
            />
          </div>

          <div className="burdens-card">
            <div className="col-md-5 px-2">
              <h5 className="burdens-card-head">Share Your Testimony</h5>
              <p>
                Share your story and encourage someone today. Let the world know
                what God has done for you!
              </p>

              <img src={note} alt="note" />
              <div className="feedback-note">
                <h6>Submission Notes</h6>
                <ul>
                  <li>All testimonies are reviewed before publishing.</li>
                  <li>
                    Featured testimonies may appear on the homepage or
                    newsletter with your permission.
                  </li>
                  <li>You can choose to remain anonymous if preferred.</li>
                </ul>
              </div>
            </div>

            <div className="col-md-5">
              <div className="submit-card">
                <h5>Share a Testimony</h5>
                <form onSubmit={handleSubmit}>
                  <div className="sharediv">
                    <input
                      type="checkbox"
                      name="is_shared_anonymously"
                      checked={formData.is_shared_anonymously}
                      onChange={handleChange}
                    />
                    <span>Share Anonymously</span>
                  </div>

                  <label className="pb-2">Your Testimony</label>
                  <textarea
                    name="testimony"
                    cols="30"
                    rows="9"
                    className="form-control"
                    placeholder="Enter your testimony"
                    value={formData.testimony}
                    onChange={handleChange}
                  ></textarea>

                  <p className="pt-3">Add to Featured Testimonies?</p>
                  <div className="sharediv">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleChange}
                    />
                    <span>
                      Yes, I'd love for my story to inspire more people.
                    </span>
                  </div>

                  <p className="pt-2">
                    <small>
                      Please read our{" "}
                      <a
                        href="https://www.rccgbournemouth.org/privacy-notice"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="privacy-link"
                      >
                        <small>
                          <u>Privacy Notice</u>
                        </small>
                      </a>{" "}
                      to learn how we protect your personal data.
                    </small>
                  </p>

                  <div className="sharebutton">
                    <button
                      type="submit"
                      className="text-white"
                      disabled={loading}
                      style={{ background: "transparent", fontSize: "14px" }}
                    >
                      {loading ? "Submitting..." : "Share Your Testimony"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Testimony;
